import React, { useEffect, useState, useMemo } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import LiveCommunications from "./components/Dashboard/LiveCommunications";
import AdvancedAudioPlayer from './components/Dashboard/AdvancedAudioPlayer';
import SettingsPage from "./components/Settings/SettingsPage";
import UserManagement from "./components/Users/index";
import LogsPage from "./components/Logs/LogsPage";
import ReportPage from "./components/ReportPage";
import UserProfile from "./components/UserProfile";
import UserGuidePage from "./components/Documentation/UserGuidePage";
import FloatingDocumentationIcon from "./components/Documentation/FloatingDocumentationIcon";
// import SetupRoute from "./components/SetupRoute"; // Disabled - setup wizard disabled for now
import axios from "axios";
import "./App.css";
import { AuthProvider } from './components/AuthContext';
import { PrivateRoute } from './components/PrivateRoute';
import LoginPage from './components/LoginPage';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logger from './utils/logger';

const CACHE_KEYS = {
  CHANNELS: 'cached_channels',
  MESSAGES: 'cached_messages',
  KEYWORDS: 'cached_keywords',
  TIMEZONE: 'cached_timezone',
  TIME_FORMAT: 'cached_time_format',
  LAST_FETCH: 'last_fetch_time'
};

// API endpoints configuration
const API_ENDPOINTS = {
  MESSAGES: '/recordings', // Change to '/records' if your API uses that endpoint
  CHANNELS: '/channels',
  SETTINGS: '/settings',
  ALERTS: '/alerts'
};

const CACHE_DURATION = 50 * 60 * 1000; // 50 minutes in milliseconds

// Cache management utilities
const getCacheSize = (key) => {
  try {
    const data = localStorage.getItem(key);
    return data ? new Blob([data]).size : 0;
  } catch (error) {
    return 0;
  }
};

const getTotalCacheSize = () => {
  try {
    let totalSize = 0;
    Object.values(CACHE_KEYS).forEach(key => {
      totalSize += getCacheSize(key);
    });
    return totalSize;
  } catch (error) {
    return 0;
  }
};

const clearOldCache = () => {
  try {
    const lastFetch = getCachedData(CACHE_KEYS.LAST_FETCH);
    if (lastFetch && (Date.now() - lastFetch > CACHE_DURATION * 2)) {
      // Clear cache if it's older than 2x the cache duration
      Object.values(CACHE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      logger.debug('Cleared old cache data');
    }
  } catch (error) {
    logger.error('Error clearing old cache:', error);
  }
};

const validateTimezone = (tz) => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch (e) {
    return false;
  }
};

// Move cache functions here, before they're used
const cacheData = (key, data) => {
  try {
    // For messages, ensure they're properly ordered before caching
    if (key === CACHE_KEYS.MESSAGES && Array.isArray(data)) {
      data = sortMessagesByTime(data);
      logger.debug(`Messages sorted and ready for caching (latest to oldest): ${data.length} messages`);
    }
    
    // Check if data is too large for localStorage
    const dataString = JSON.stringify(data);
    const dataSize = new Blob([dataString]).size;
    const maxSize = 4 * 1024 * 1024; // 4MB limit to be safe
    
    if (dataSize > maxSize) {
      logger.warn(`Data for ${key} is too large (${(dataSize / 1024 / 1024).toFixed(2)}MB), implementing size reduction`);
      
      // For messages, keep only the most recent ones (first in array since they're sorted latest to oldest)
      if (key === CACHE_KEYS.MESSAGES && Array.isArray(data)) {
        const maxMessages = 1000; // Keep only first 1000 messages (most recent)
        const reducedData = data.slice(0, maxMessages);
        const reducedString = JSON.stringify(reducedData);
        const reducedSize = new Blob([reducedString]).size;
        
        if (reducedSize <= maxSize) {
          localStorage.setItem(key, reducedString);
          logger.debug(`Successfully cached ${reducedData.length} most recent messages (reduced from ${data.length})`);
        } else {
          // If still too large, keep even fewer messages
          const furtherReduced = data.slice(0, 500);
          localStorage.setItem(key, JSON.stringify(furtherReduced));
          logger.debug(`Successfully cached ${furtherReduced.length} most recent messages (further reduced)`);
        }
      } else {
        // For other data types, try to store a subset or skip caching
        logger.warn(`Skipping cache for ${key} due to size constraints`);
        return false;
      }
    } else {
      localStorage.setItem(key, dataString);
    }
    return true;
  } catch (error) {
    if (error.name === 'QuotaExceededError') {
      logger.warn(`Storage quota exceeded for ${key}, attempting cleanup`);
      
      // Try to clear some old cache entries
      try {
        const keysToClear = [CACHE_KEYS.MESSAGES, CACHE_KEYS.CHANNELS, CACHE_KEYS.KEYWORDS];
        keysToClear.forEach(cacheKey => {
          if (cacheKey !== key) {
            localStorage.removeItem(cacheKey);
          }
        });
        
        // Try caching again with reduced data
        if (key === CACHE_KEYS.MESSAGES && Array.isArray(data)) {
          const reducedData = data.slice(0, 500); // Keep first 500 (most recent)
          localStorage.setItem(key, JSON.stringify(reducedData));
          logger.debug(`Successfully cached ${reducedData.length} most recent messages after cleanup`);
          return true;
        }
      } catch (cleanupError) {
        logger.error(`Failed to cleanup and retry caching for ${key}:`, cleanupError);
      }
    }
    logger.error(`Error caching ${key}:`, error);
    return false;
  }
};

const getCachedData = (key) => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    logger.error(`Error retrieving ${key} from cache:`, error);
    return null;
  }
};

// Function to log cache usage for debugging
const logCacheUsage = () => {
  try {
    const totalSize = getTotalCacheSize();
    logger.debug(`Total cache size: ${(totalSize / 1024 / 1024).toFixed(2)}MB`);
    
    Object.entries(CACHE_KEYS).forEach(([name, key]) => {
      const size = getCacheSize(key);
      if (size > 0) {
        logger.debug(`${name}: ${(size / 1024 / 1024).toFixed(2)}MB`);
      }
    });
  } catch (error) {
    logger.error('Error logging cache usage:', error);
  }
};

// Function to get cache statistics
const getCacheStats = () => {
  try {
    const stats = {};
    Object.entries(CACHE_KEYS).forEach(([name, key]) => {
      const data = getCachedData(key);
      if (data) {
        if (Array.isArray(data)) {
          stats[name] = { count: data.length, size: getCacheSize(key) };
        } else if (typeof data === 'object') {
          stats[name] = { count: Object.keys(data).length, size: getCacheSize(key) };
        } else {
          stats[name] = { count: 1, size: getCacheSize(key) };
        }
      }
    });
    return stats;
  } catch (error) {
    logger.error('Error getting cache stats:', error);
    return {};
  }
};

// Function to check if cache is healthy
const isCacheHealthy = () => {
  try {
    const stats = getCacheStats();
    const totalSize = getTotalCacheSize();
    const maxSize = 10 * 1024 * 1024; // 10MB limit
    
    return {
      healthy: totalSize < maxSize,
      totalSize,
      maxSize,
      stats
    };
  } catch (error) {
    logger.error('Error checking cache health:', error);
    return { healthy: false, error: error.message };
  }
};

// Function to sort messages by time (latest to oldest)
const sortMessagesByTime = (messages) => {
  if (!Array.isArray(messages)) return messages;
  
  return messages.sort((a, b) => {
    const timeA = new Date(a.time || a.timestamp || 0).getTime();
    const timeB = new Date(b.time || b.timestamp || 0).getTime();
    return timeB - timeA; // Most recent first (latest to oldest)
  });
};

// Function to process and order messages from API response
const processMessagesFromAPI = (apiData, timezone, edgeServerEndpoint) => {
  if (!Array.isArray(apiData)) return [];
  
  const processedMessages = apiData.map(item => ({
    channel: item.channel_id.toString(),
    team: `Channel ${item.channel_id}`,
    time: item.timestamp,
    timezone: timezone,
    status: item.hasOwnProperty("status") ? item.status : "new",
    id: item.id,
    url: `${edgeServerEndpoint}/${item.filename.replace(/\\/g, '/')}`,
    message: item.transcription || "No transcription available",
    isNew: true,
  }));
  
  // Sort messages by time (latest to oldest)
  return sortMessagesByTime(processedMessages);
};

const App = () => {
  const [channels, setChannels] = useState({});
  const [messages, setMessages] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showServerErrorModal, setShowServerErrorModal] = useState(false);
  const [cacheCleared, setCacheCleared] = useState(false);
  const [endpointInput, setEndpointInput] = useState("");
  const [validationStatus, setValidationStatus] = useState("pending");
  const [edgeServerEndpoint, setEdgeServerEndpoint] = useState(
    localStorage.getItem("EDGE_SERVER_ENDPOINT") || process.env.REACT_APP_EDGE_SERVER_ENDPOINT || ""
  );
  
  // Add cache debugging to window object for development
  if (process.env.NODE_ENV === 'development') {
    window.debugCache = {
      getStats: getCacheStats,
      isHealthy: isCacheHealthy,
      clearCache: () => {
        Object.values(CACHE_KEYS).forEach(key => localStorage.removeItem(key));
        logger.debug('Cache cleared');
      },
      logUsage: logCacheUsage,
      getLatestMessages: (count = 10) => {
        const messages = getCachedData(CACHE_KEYS.MESSAGES) || [];
        return messages.slice(0, count);
      },
      getMessageCount: () => {
        const messages = getCachedData(CACHE_KEYS.MESSAGES) || [];
        return messages.length;
      },
      sortMessages: sortMessagesByTime
    };
  }
  
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return JSON.parse(localStorage.getItem("isDarkMode")) || false;
  });
  
  const [timezone, setTimezone] = useState(() => {
    const cachedTimezone = getCachedData(CACHE_KEYS.TIMEZONE);
    return cachedTimezone && validateTimezone(cachedTimezone) ? cachedTimezone : "Etc/UTC";
  });

  const [timeFormat, setTimeFormat] = useState(() => {
    const cachedTimeFormat = getCachedData(CACHE_KEYS.TIME_FORMAT);
    return cachedTimeFormat || "24h"; // Default to 24-hour format
  });

  const [branding, setBranding] = useState({
    organization_name: 'Boondock Edge Server'
  });

  const [reverseSort, setReverseSort] = useState(false);

  const toggleTheme = () => setIsDarkMode(prev => !prev);

  const setTimezoneWithLogging = (newTz) => {
    logger.debug(`Timezone changed from ${timezone} to ${newTz}`);
    setTimezone(newTz);
  };

  const setTimeFormatWithLogging = (newFormat) => {
    logger.debug(`Time format changed from ${timeFormat} to ${newFormat}`);
    setTimeFormat(newFormat);
    cacheData(CACHE_KEYS.TIME_FORMAT, newFormat);
  };

  // Fetch branding data and update document title
  const fetchBrandingData = async () => {
    try {
      const response = await axios.get(`${edgeServerEndpoint}/branding`);
      const brandingData = response.data;
      setBranding(brandingData);
      
      // Update document title with organization name
      if (brandingData.organization_name) {
        document.title = brandingData.organization_name;
      } else {
        // Fallback to default title
        document.title = 'Boondock Edge Server';
      }
    } catch (error) {
      logger.error('Error fetching branding data:', error);
      // Keep default title if fetch fails
      document.title = 'Boondock Edge Server';
    }
  };

  useEffect(() => {
    localStorage.setItem("isDarkMode", JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  // Update document title when branding changes
  useEffect(() => {
    if (branding.organization_name) {
      document.title = branding.organization_name;
    }
  }, [branding.organization_name]);

  // Memoized processed messages
  const processedMessages = useMemo(() => {
    return messages.map(message => ({
      ...message,
      localTime: new Date(message.time).toLocaleString('en-US', { 
        timeZone: timezone,
        hour12: timeFormat === "12h"
      })
    }));
  }, [messages, timezone, timeFormat]);

  const loadCachedData = () => {
    const cachedChannels = getCachedData(CACHE_KEYS.CHANNELS);
    const cachedMessages = getCachedData(CACHE_KEYS.MESSAGES);
    const cachedKeywords = getCachedData(CACHE_KEYS.KEYWORDS);
    const cachedTimezone = getCachedData(CACHE_KEYS.TIMEZONE);
    const cachedTimeFormat = getCachedData(CACHE_KEYS.TIME_FORMAT);

    if (cachedChannels) {
      logger.debug(`Loaded ${Object.keys(cachedChannels).length} channels from cache`);
      setChannels(cachedChannels);
    }
    if (cachedMessages) {
      // Ensure cached messages are properly ordered (latest to oldest)
      const orderedMessages = sortMessagesByTime(cachedMessages);
      logger.debug(`Loaded ${orderedMessages.length} messages from cache (ordered latest to oldest)`);
      setMessages(orderedMessages);
    }
    if (cachedKeywords) {
      logger.debug(`Loaded ${cachedKeywords.length} keywords from cache`);
      setKeywords(cachedKeywords);
    }
    if (cachedTimezone && validateTimezone(cachedTimezone)) {
      setTimezoneWithLogging(cachedTimezone);
    }
    if (cachedTimeFormat) {
      setTimeFormatWithLogging(cachedTimeFormat);
    }
  };

  const isCacheValid = () => {
    const lastFetch = getCachedData(CACHE_KEYS.LAST_FETCH);
    return lastFetch && (Date.now() - lastFetch < CACHE_DURATION);
  };

  // Check if there's any cached data available
  const hasCachedData = () => {
    const cachedMessages = getCachedData(CACHE_KEYS.MESSAGES);
    const cachedChannels = getCachedData(CACHE_KEYS.CHANNELS);
    // Return true if we have at least messages or channels cached
    return (cachedMessages && cachedMessages.length > 0) || (cachedChannels && Object.keys(cachedChannels).length > 0);
  };

  // Endpoint validation
  const validateEndpoint = async (url) => {
    try {
      const response = await axios.get(`${url}/ping`, { timeout: 5000 });
      return response.status === 200;
    } catch (error) {
      logger.error("Endpoint validation error:", error);
      return false;
    }
  };

  // Check if server is still working
  const checkServerHealth = async () => {
    if (!edgeServerEndpoint) return false;
    try {
      const response = await axios.get(`${edgeServerEndpoint}/ping`, { timeout: 5000 });
      return response.status === 200;
    } catch (error) {
      logger.error("Server health check failed:", error);
      return false;
    }
  };

  // Clear all cache
  const clearAllCache = async () => {
    try {
      Object.values(CACHE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      logger.debug('All cache cleared');
      // Also clear messages and channels from state
      setMessages([]);
      setChannels({});
      setKeywords([]);
      
      // After clearing cache, check if server is still down
      // If server is down and cache is cleared, show the modal
      const isServerHealthy = await checkServerHealth();
      if (!isServerHealthy) {
        setShowServerErrorModal(true);
      }
    } catch (error) {
      logger.error('Error clearing cache:', error);
    }
  };

  const handleEndpointSubmit = async () => {
    setValidationStatus("validating");
    setError(null);
    
    try {
      new URL(endpointInput);
      const isValid = await validateEndpoint(endpointInput);
      
      if (isValid) {
        setEdgeServerEndpoint(endpointInput);
        localStorage.setItem("EDGE_SERVER_ENDPOINT", endpointInput);
        setShowModal(false);
        setValidationStatus("pending");
      } else {
        throw new Error("Please check your server is running or not");
      }
    } catch (error) {
      setValidationStatus("failed");
      setError(error.message);
    }
  };

  // Initial load
  useEffect(() => {
    const initialize = async () => {
      if (!edgeServerEndpoint) {
        setShowModal(true);
        return;
      }

      // Check setup status first - but don't redirect here
      // Let the router handle it via SetupRoute component
      // This prevents infinite redirect loops

      // Load cached data first for immediate rendering
      loadCachedData();
      
      // Check server health first
      const isServerHealthy = await checkServerHealth();
      if (!isServerHealthy) {
        // Only show modal if there's no cached data available
        if (!hasCachedData()) {
          setShowServerErrorModal(true);
        }
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        // Always fetch fresh data in background, regardless of cache validity
        // This ensures we have the most up-to-date information
        await fetchAllData(true); // Show loading on initial load only
        // Always fetch branding data to update title
        await fetchBrandingData();
        setShowServerErrorModal(false); // Hide error modal if initialization succeeds
      } catch (error) {
        logger.error("Initialization error:", error);
        // Check server health on error
        const isServerHealthyOnError = await checkServerHealth();
        if (!isServerHealthyOnError && !hasCachedData()) {
          setShowServerErrorModal(true);
        }
        // Only show error if we don't have any cached data to fall back on
        if (!getCachedData(CACHE_KEYS.MESSAGES)) {
          setError("Failed to initialize application");
        } else {
          logger.warn("Using cached data due to fetch error:", error);
        }
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [edgeServerEndpoint]);

  // Data fetching
  const fetchAllData = async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      // Fetch data from API endpoints
      const [channelsRes, messagesRes, keywordsRes] = await Promise.all([
        axios.get(`${edgeServerEndpoint}${API_ENDPOINTS.CHANNELS}`),
        axios.get(`${edgeServerEndpoint}${API_ENDPOINTS.MESSAGES}`), // This fetches the messages/recordings
        axios.get(`${edgeServerEndpoint}${API_ENDPOINTS.SETTINGS}`)
      ]);

      // Validate and log data formats
      logger.debug('Channels response type:', typeof channelsRes.data, 'Is array:', Array.isArray(channelsRes.data));
      logger.debug('Messages response type:', typeof messagesRes.data, 'Is array:', Array.isArray(messagesRes.data));
      logger.debug('Keywords response type:', typeof keywordsRes.data);

      // Validate that data is in expected format
      if (!Array.isArray(channelsRes.data)) {
        logger.warn('Channels data is not an array:', channelsRes.data);
        // If it's an object with a channels property, use that
        if (typeof channelsRes.data === 'object' && channelsRes.data.channels && Array.isArray(channelsRes.data.channels)) {
          channelsRes.data = channelsRes.data.channels;
        } else {
          throw new Error(`Invalid channels data format - expected array, got ${typeof channelsRes.data}`);
        }
      }
      if (!Array.isArray(messagesRes.data)) {
        logger.warn('Messages data is not an array:', messagesRes.data);
        // If it's an object with a messages property, use that
        if (typeof messagesRes.data === 'object' && messagesRes.data.messages && Array.isArray(messagesRes.data.messages)) {
          messagesRes.data = messagesRes.data.messages;
        } else {
          throw new Error(`Invalid messages data format - expected array, got ${typeof messagesRes.data}`);
        }
      }
      if (!keywordsRes.data || typeof keywordsRes.data !== 'object') {
        logger.warn('Settings data format invalid:', keywordsRes.data);
        throw new Error('Invalid settings data format - expected object');
      }

      // Process channels
      const channelsData = channelsRes.data.reduce((acc, channel) => {
        if (channel.status !== 'disabled') {
          acc[channel.id] = {
            name: channel.name,
            status: channel.status,
            id: channel.id,
            driver: channel.driver,
            person: channel.person,
            car: channel.car,
            color: channel.color,
            background_color: channel.background_color,
            team_color: channel.team_color,
            silence: channel.silence,
            mac: channel.mac,
            sensitivity: channel.sensitivity,
            min_rec: channel.min_rec,
            max_rec: channel.max_rec,
            audio_gain: channel.audio_gain,
            tag: channel.tag,
            position: channel.position || 0,
            isActive: true,
          };
        }
        return acc;
      }, {});

      // Process messages and ensure they're ordered latest to oldest
      const messagesData = processMessagesFromAPI(messagesRes.data, timezone, edgeServerEndpoint);

      // Only update state if data has actually changed to prevent unnecessary re-renders
      // Preserve position values from previous state if they exist
      setChannels(prevChannels => {
        const prevKeys = Object.keys(prevChannels).sort().join(',');
        const newKeys = Object.keys(channelsData).sort().join(',');
        
        // Merge new data with previous positions to preserve user-set positions
        const mergedChannels = { ...channelsData };
        for (const key in mergedChannels) {
          // Ensure position is always set from API data (0 is a valid position)
          const newPosition = mergedChannels[key].position !== undefined && mergedChannels[key].position !== null 
            ? parseInt(mergedChannels[key].position, 10) 
            : 0;
          mergedChannels[key].position = newPosition;
        }
        
        if (prevKeys !== newKeys) return mergedChannels;
        // Deep compare channel objects (excluding position for comparison)
        for (const key in mergedChannels) {
          const prevChannel = { ...prevChannels[key] };
          const newChannel = { ...mergedChannels[key] };
          // Remove position from comparison since we're preserving it
          delete prevChannel.position;
          delete newChannel.position;
          if (JSON.stringify(prevChannel) !== JSON.stringify(newChannel)) {
            return mergedChannels;
          }
        }
        // If only positions changed, still update to preserve them
        const positionsChanged = Object.keys(mergedChannels).some(key => 
          prevChannels[key] && 
          prevChannels[key].position !== mergedChannels[key].position
        );
        if (positionsChanged) {
          return mergedChannels;
        }
        return prevChannels; // No changes, return previous to prevent re-render
      });

      setMessages(prevMessages => {
        // Compare message arrays - check length and IDs
        if (prevMessages.length !== messagesData.length) return messagesData;
        // Compare first and last message IDs to detect changes
        if (prevMessages.length > 0 && messagesData.length > 0) {
          if (prevMessages[0]?.id !== messagesData[0]?.id || 
              prevMessages[prevMessages.length - 1]?.id !== messagesData[messagesData.length - 1]?.id) {
            return messagesData;
          }
        }
        return prevMessages; // No changes, return previous to prevent re-render
      });

      setKeywords(prevKeywords => {
        const prevStr = JSON.stringify(prevKeywords);
        const newStr = JSON.stringify(keywordsRes.data.keywords);
        if (prevStr !== newStr) return keywordsRes.data.keywords;
        return prevKeywords; // No changes, return previous to prevent re-render
      });

      // Handle timezone
      const newTimezone = keywordsRes.data.global_timezone || "Etc/UTC";
      if (validateTimezone(newTimezone)) {
        setTimezoneWithLogging(newTimezone);
        cacheData(CACHE_KEYS.TIMEZONE, newTimezone);
      } else {
        logger.warn(`Invalid timezone received: ${newTimezone}, falling back to UTC`);
        setTimezoneWithLogging("Etc/UTC");
        cacheData(CACHE_KEYS.TIMEZONE, "Etc/UTC");
      }

      // Update cache with fresh data
      const cacheResults = {
        channels: cacheData(CACHE_KEYS.CHANNELS, channelsData),
        messages: cacheData(CACHE_KEYS.MESSAGES, messagesData),
        keywords: cacheData(CACHE_KEYS.KEYWORDS, keywordsRes.data.keywords),
        timestamp: cacheData(CACHE_KEYS.LAST_FETCH, Date.now())
      };
      
      // Log cache update results
      logger.debug('Cache update results:', {
        channels: cacheResults.channels ? 'success' : 'failed',
        messages: cacheResults.messages ? 'success' : 'failed',
        keywords: cacheResults.keywords ? 'success' : 'failed',
        timestamp: cacheResults.timestamp ? 'success' : 'failed',
        messageCount: messagesData.length
      });
      
      // Log cache usage for debugging
      logCacheUsage();
      
      setError(null);
    } catch (error) {
      logger.error("Data fetching error:", error);
      setError("Failed to fetch data");
      throw error;
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  // Server health check
  useEffect(() => {
    if (!edgeServerEndpoint) return;

    const healthCheckInterval = setInterval(async () => {
      const isServerHealthy = await checkServerHealth();
      if (!isServerHealthy) {
        // Only show modal if there's no cached data available
        if (!hasCachedData()) {
          setShowServerErrorModal(true);
        }
      } else {
        setShowServerErrorModal(false);
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(healthCheckInterval);
  }, [edgeServerEndpoint]);

  // Track if filters are active (to pause auto-updates)
  const [areFiltersActive, setAreFiltersActive] = useState(false);

  // Periodic updates
  useEffect(() => {
    if (!edgeServerEndpoint) return;

    const updateInterval = setInterval(async () => {
      // Skip update if audio is currently playing to prevent screen flicker
      const audioIsPlaying = localStorage.getItem('audioIsPlaying') === 'true';
      if (audioIsPlaying) {
        return; // Skip this update cycle
      }

      // Skip update if filters are active
      if (areFiltersActive) {
        return; // Skip this update cycle
      }

      try {
        // First check if server is still working
        const isServerHealthy = await checkServerHealth();
        if (!isServerHealthy) {
          // Only show modal if there's no cached data available
          if (!hasCachedData()) {
            setShowServerErrorModal(true);
          }
          return; // Don't proceed with data fetch if server is down
        }

        // Check cache health before fetching new data
        const cacheHealth = isCacheHealthy();
        if (!cacheHealth.healthy) {
          logger.warn('Cache health check failed:', cacheHealth);
          // Clear cache if it's too large
          if (cacheHealth.totalSize > cacheHealth.maxSize) {
            logger.debug('Clearing cache due to size limit');
            Object.values(CACHE_KEYS).forEach(key => {
              localStorage.removeItem(key);
            });
          }
        }
        
        // Clear old cache before fetching new data
        clearOldCache();
        await fetchAllData(false); // Don't show loading spinner during periodic updates
        setShowServerErrorModal(false); // Hide error modal if fetch succeeds
      } catch (error) {
        logger.error("Update error:", error);
        // Check server health on error
        const isServerHealthy = await checkServerHealth();
        if (!isServerHealthy && !hasCachedData()) {
          setShowServerErrorModal(true);
        }
      }
    }, 5000);

    return () => clearInterval(updateInterval);
  }, [edgeServerEndpoint, areFiltersActive]);

  if (loading && !Object.keys(channels).length) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <>
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className={`w-full max-w-md p-6 mx-4 rounded-lg shadow-xl transition-colors duration-300 ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="mb-4">
              <h2 className={`text-xl font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-800'
              }`}>
                {validationStatus === "failed" 
                  ? "Connection Failed" 
                  : "Enter Edge Server Endpoint"}
              </h2>
              <p className={`mt-2 text-sm transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {validationStatus === "failed"
                  ? "Please check your server is running or not"
                  : "Please provide the URL for your Edge Server endpoint to continue."}
              </p>
              {error && (
                <p className="mt-2 text-sm text-red-500">{error}</p>
              )}
            </div>

            <div className="mt-4">
              <input
                type="url"
                placeholder="https://your-server.com"
                value={endpointInput}
                onChange={(e) => setEndpointInput(e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-300 ${
                  validationStatus === "failed" 
                    ? "border-red-500" 
                    : isDarkMode 
                      ? "border-gray-600 bg-gray-700 text-white placeholder-gray-400" 
                      : "border-gray-300 bg-white text-gray-900"
                }`}
              />
            </div>

            <div className="mt-6">
              <button
                onClick={handleEndpointSubmit}
                disabled={validationStatus === "validating"}
                className="w-full px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:bg-blue-400"
              >
                {validationStatus === "validating" 
                  ? "Connecting..." 
                  : validationStatus === "failed"
                  ? "Try Again"
                  : "Connect to Server"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showServerErrorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className={`w-full max-w-md mx-4 rounded-xl shadow-2xl border overflow-hidden transition-colors duration-300 ${
            isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            {/* Header with icon */}
            <div className={`px-6 py-4 border-b transition-colors duration-300 ${
              isDarkMode ? 'bg-red-900/30 border-red-800/50' : 'bg-red-50 border-red-100'
            }`}>
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300 ${
                    isDarkMode ? 'bg-red-900/50' : 'bg-red-100'
                  }`}>
                    <svg className={`w-6 h-6 transition-colors duration-300 ${
                      isDarkMode ? 'text-red-400' : 'text-red-600'
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h2 className={`text-xl font-bold transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    Server Connection Error
                  </h2>
                  <p className={`text-sm mt-0.5 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    Unable to reach the server
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-5">
              <div className="mb-6">
                <p className={`text-sm leading-relaxed transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Please check your server connection or connect to admin. The server may be temporarily unavailable or experiencing issues.
                </p>
              </div>

              {/* Cache info */}
              <div className={`mb-6 p-3 rounded-lg border transition-colors duration-300 ${
                isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <svg className={`w-4 h-4 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                    </svg>
                    <span className={`text-xs font-medium transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>Cache Status</span>
                  </div>
                  <span className={`text-xs transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {getCachedData(CACHE_KEYS.MESSAGES)?.length || 0} messages cached
                  </span>
                </div>
              </div>

              {/* Action buttons */}
              <div className="space-y-3">
                <button
                  onClick={async () => {
                    const isHealthy = await checkServerHealth();
                    if (isHealthy) {
                      setShowServerErrorModal(false);
                    }
                  }}
                  className="w-full px-4 py-3 text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 font-medium shadow-sm hover:shadow-md flex items-center justify-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Retry Connection</span>
                </button>
                
                <button
                  onClick={async () => {
                    await clearAllCache();
                    setCacheCleared(true);
                    setTimeout(() => {
                      setCacheCleared(false);
                    }, 2000);
                  }}
                  className={`w-full px-4 py-3 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 font-medium shadow-sm hover:shadow-md flex items-center justify-center space-x-2 ${
                    cacheCleared 
                      ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' 
                      : 'bg-gray-600 hover:bg-gray-700 focus:ring-gray-500'
                  }`}
                >
                  {cacheCleared ? (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Cache Cleared!</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span>Clear Cache</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AuthProvider>
 
     
        <Router>
          <Routes>
            {/* Setup route disabled - defaults are created automatically */}
            {/* <Route path="/setup" element={
              <SetupRoute 
                edgeServerEndpoint={edgeServerEndpoint}
                isDarkMode={isDarkMode}
              />
            } /> */}
            <Route path="/login" element={
              <LoginPage 
                toggleTheme={toggleTheme} 
                isDarkMode={isDarkMode} 
                setIsDarkMode={setIsDarkMode}   
                edgeServerEndpoint={edgeServerEndpoint}  
              />
            } />
            <Route path="/" element={
              <PrivateRoute>
                <LiveCommunications
                  timezone={timezone}
                  timeFormat={timeFormat}
                  isDarkMode={isDarkMode}
                  edgeServerEndpoint={edgeServerEndpoint}
                  channels={channels}
                  setChannels={setChannels}
                  setMessages={setMessages}
                  setIsDarkMode={setIsDarkMode}
                  toggleTheme={toggleTheme}
                  channels={channels} 
                  messages={processedMessages}
                  keywords={keywords}
                  reverseSort={reverseSort}
                  setReverseSort={setReverseSort}
                  onFiltersActiveChange={setAreFiltersActive}
                />
              </PrivateRoute>
            } />
            <Route path="/settings" element={
              <PrivateRoute>
                <SettingsPage 
                  isDarkMode={isDarkMode} 
                  timezone={timezone} 
                  timeFormat={timeFormat}
                  setTimeFormat={setTimeFormatWithLogging}
                  reverseSort={reverseSort} 
                  setReverseSort={setReverseSort}
                  onSettingsChange={() => fetchAllData()}
                />
              </PrivateRoute>
            } />
            <Route path="/users" element={
              <PrivateRoute>
                <UserManagement isDarkMode={isDarkMode} />
              </PrivateRoute>
            } />
            <Route path="/profile" element={
              <PrivateRoute>
                <UserProfile 
                  isDarkMode={isDarkMode} 
                  edgeServerEndpoint={edgeServerEndpoint}
                />
              </PrivateRoute>
            } />

<Route path="/advanced-player" element={<AdvancedAudioPlayer isDarkMode={isDarkMode} timeFormat={timeFormat} />} />
            <Route path="/logs" element={
              <PrivateRoute>
                <LogsPage edgeServerEndpoint={edgeServerEndpoint} timezone={timezone} timeFormat={timeFormat} />
              </PrivateRoute>
            } />
            <Route path="/report" element={
              <PrivateRoute>
                <ReportPage isDarkMode={isDarkMode} timeFormat={timeFormat} />
              </PrivateRoute>
            } />
            <Route path="/user-guide" element={
              <PrivateRoute>
                <UserGuidePage isDarkMode={isDarkMode} />
              </PrivateRoute>
            } />
          </Routes>
          <FloatingDocumentationIcon isDarkMode={isDarkMode} />
        </Router>
        <ToastContainer 
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          limit={3}
          enableMultiContainer={false}
        />
      </AuthProvider>
    </>
  );
};
export default App;